import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { StudioPermissionMap } from "@dnceclub/shared";
import { useAuth } from "./auth-context";
import { supabase } from "../supabase";

interface StudioContextType {
  currentStudioId: string | null;
  loading: boolean;
  isOwner: boolean;
  currentStudioPermissions: StudioPermissionMap | null;
}

const StudioContext = createContext<StudioContextType | undefined>(undefined);

interface StudioContextProviderProps {
  studioId?: string; // Optional — will be resolved from user context if not provided
  children: React.ReactNode;
}

export function StudioContextProvider({
  studioId,
  children,
}: StudioContextProviderProps) {
  const { user, profile } = useAuth();
  const [resolvedStudioId, setResolvedStudioId] = useState<string | null>(
    studioId ?? null
  );
  const [loading, setLoading] = useState(!studioId);

  useEffect(() => {
    if (studioId) {
      setResolvedStudioId(studioId);
      setLoading(false);
      return;
    }

    // Owner: fetch their studio from supabase
    if (profile?.is_owner && user?.id) {
      setLoading(true);
      supabase
        .from("studios")
        .select("id")
        .eq("owner_id", user.id)
        .single()
        .then(({ data }) => {
          if (data) setResolvedStudioId(data.id);
          setLoading(false);
        });
      return;
    }

    // Staff: use first staffRole's studioId
    if (profile?.staffRoles && profile.staffRoles.length > 0) {
      setResolvedStudioId(profile.staffRoles[0].studioId);
      setLoading(false);
    }
  }, [studioId, profile, user?.id]);

  const value = useMemo(() => {
    const isOwner = profile?.is_owner === true;

    // Find staff role for this studio (if not owner)
    const staffRole =
      !isOwner && resolvedStudioId
        ? profile?.staffRoles?.find((r) => r.studioId === resolvedStudioId)
        : null;

    return {
      currentStudioId: resolvedStudioId,
      loading,
      isOwner,
      currentStudioPermissions:
        (staffRole?.permissions as StudioPermissionMap) ?? null,
    };
  }, [resolvedStudioId, loading, profile]);

  return (
    <StudioContext.Provider value={value}>{children}</StudioContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useStudioContext() {
  const context = useContext(StudioContext);
  if (context === undefined) {
    throw new Error(
      "useStudioContext must be used within a StudioContextProvider"
    );
  }
  return context;
}
